import { ReactNode } from "react";
import { useAuthStore } from "@/store/auth.store";

/**
 * Props for PermissionGate component
 */
export interface PermissionGateProps {
  /**
   * Permission name or array of names (any match grants access)
   */
  permission: string | string[];

  /**
   * Content to render if user has permission
   */
  children: ReactNode;

  /**
   * Optional fallback if user lacks permission
   */
  fallback?: ReactNode;

  /**
   * If true, user must have ALL permissions (default: any)
   */
  requireAll?: boolean;
}

/**
 * Component for permission-based conditional rendering.
 *
 * Replaces Task 2's v-permission Vue directive.
 * Uses Zustand auth store for permission state.
 *
 * @example
 * <PermissionGate permission="bookings.create">
 *   <CreateBookingButton />
 * </PermissionGate>
 *
 * @see REUSE_ALL_REFACTORED.md Ciclo 8
 */
export function PermissionGate({
  permission,
  children,
  fallback = null,
  requireAll = false,
}: PermissionGateProps) {
  const { user } = useAuthStore();

  // Get user permissions from store (properly typed now)
  const userPermissions = user?.permissions ?? [];

  // Normalize permission to array
  const requiredPermissions = Array.isArray(permission)
    ? permission
    : [permission];

  // Check permission based on requireAll flag
  const hasPermission = requireAll
    ? requiredPermissions.every((p) => userPermissions.includes(p))
    : requiredPermissions.some((p) => userPermissions.includes(p));

  if (!hasPermission) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Hook version for programmatic permission checks.
 *
 * @example
 * const { can, canAll } = usePermissions();
 * if (can('bookings.create')) { ... }
 */
// eslint-disable-next-line react-refresh/only-export-components
export function usePermissions() {
  const { user } = useAuthStore();
  const userPermissions = user?.permissions ?? [];

  /**
   * Check if user has any of the given permissions
   */
  const can = (permission: string | string[]): boolean => {
    const permissions = Array.isArray(permission) ? permission : [permission];
    return permissions.some((p) => userPermissions.includes(p));
  };

  /**
   * Check if user has all of the given permissions
   */
  const canAll = (permissions: string[]): boolean => {
    return permissions.every((p) => userPermissions.includes(p));
  };

  return { can, canAll, permissions: userPermissions };
}
